import React, { useEffect, useMemo } from 'react'
import { Redirect, useLocation, useRouteMatch } from 'react-router-dom'
import { PageLoader } from 'components/loader'
import Sidebar from 'components/admin/sidebar'
import { post_event } from 'utils/analytics'
import {
  ROOT_PATH,
  LOGIN_PATH,
  ADMIN_ROOT_PATH,
  ADMIN_LEAGUE_REPORT,
  ADMIN_ACTIVITIES_HOME_PATH,
  ADMIN_READING_ASSESSMENT_HOME_V2,
  ADMIN_REPORTS_PATH,
  ADMIN_ENTITY_USERS_PATH,
  ADMIN_BOOKS_MANAGEMENT_PATH,
  ADMIN_BOOK_INDICATION_PATH,
  ADMIN_SUMMARY_REPORT_V3_PATH
} from 'routes/paths'
import { getCookie, removeCookieOnDomain } from 'utils/cookies'
import { ServerError as ServerErrorScreen } from 'screens/http-status'
import { setHotJarUserData } from 'utils/ui-trackers/hotjar'
import redirects, { type RedirectsPrevLocationState } from 'routes/redirects'
import { useLanguageCheck } from 'utils/hooks/use-language-check'
import { requestErrorStatus } from 'api/error'
import { useNotInArvoreToast } from 'routes/hooks/use-not-in-arvore-toast'
import useFeatureToggles from 'utils/hooks/use-feature-toggles'
import { useSuperAutorEligible } from 'utils/hooks/use-super-autor-eligible/useSuperAutorEligible'
import HeaderModular from 'components/header-modular'
import SidebarV2 from 'components/admin-v2/sidebar-v2'
import ContentAdminV2 from 'components/admin-v2/content/content-admin-v2'
import { useUserInfo } from 'utils/hooks/use-userInfo'
import useSetUserToThirdParty from 'utils/hooks/use-set-user-to-third-party'
import NewVersionBanner from 'components/new-version-banner'

type PrivateAdminRouteProps = {
  withoutLayout?: boolean
  sidebarMinimized?: boolean
  isResponsive?: boolean
  component: React.ComponentType
}

const PrivateAdminRoute: React.FC<PrivateAdminRouteProps> = ({
  component: Component,
  withoutLayout,
  sidebarMinimized,
  isResponsive
}) => {
  const { path } = useRouteMatch()
  const location = useLocation<RedirectsPrevLocationState>()
  const { featureToggles } = useFeatureToggles()
  const { isSuperAutorEligible, hasCriarModuleAndFlag } =
    useSuperAutorEligible()
  const sidebarV2 =
    featureToggles?.paranaNotice || featureToggles?.adminPanelV2Rollout

  const { user, loading, error } = useUserInfo({
    errorPolicy: 'all'
  })

  const shouldShowNewVersionBanner = useMemo(() => {
    const currentPath = location.pathname

    const allowedPaths = [
      ADMIN_ROOT_PATH,
      ADMIN_LEAGUE_REPORT,
      `${ADMIN_ROOT_PATH}/relatorios#indicados`,
      ADMIN_ACTIVITIES_HOME_PATH,
      ADMIN_READING_ASSESSMENT_HOME_V2,
      ADMIN_REPORTS_PATH,
      ADMIN_BOOKS_MANAGEMENT_PATH,
      ADMIN_BOOK_INDICATION_PATH
    ]

    return allowedPaths.some(path => currentPath === path)
  }, [location.pathname])

  useSetUserToThirdParty(user)

  useLanguageCheck()

  useEffect(() => {
    post_event('visit', location.pathname)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path])

  useEffect(() => {
    if (isResponsive) {
      document
        .querySelector<HTMLMetaElement>('meta[name="viewport"]')
        .setAttribute('content', 'width=device-width, initial-scale=1')
    } else {
      document
        .querySelector<HTMLMetaElement>('meta[name="viewport"]')
        .setAttribute('content', '')
    }
  }, [isResponsive])

  useNotInArvoreToast(user)

  if (Boolean(getCookie('access_token')) === false) {
    return (
      <Redirect
        to={{
          pathname: LOGIN_PATH,
          search: `redirectTo=${encodeURIComponent(location.pathname)}`
        }}
      />
    )
  }

  if (document.body.className.indexOf('admin') === -1) {
    document.body.className += 'admin'
  }

  if (loading && !user.id) {
    return <PageLoader />
  }

  if (user.id) {
    const isTeacherOrAdmin =
      user?.teacher !== null && user?.teacher?.id !== null

    setHotJarUserData(user)

    if (isTeacherOrAdmin === false) {
      return <Redirect to={ROOT_PATH} />
    }

    if (
      !user.isAdmin &&
      isSuperAutorEligible &&
      location.pathname.includes(ADMIN_ENTITY_USERS_PATH)
    ) {
      window.location.href = ADMIN_SUMMARY_REPORT_V3_PATH
      return <PageLoader />
    }

    const locationToRedirect = redirects.getLocationToRedirect({
      user: user,
      routePath: path,
      prevState: location.state,
      isSuperAutorEligible: hasCriarModuleAndFlag
    })

    if (locationToRedirect.pathname) {
      return <Redirect to={locationToRedirect} />
    }

    if (withoutLayout) {
      return <Component />
    }

    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <HeaderModular />

        <div className="flex-1 h-full lg:pt-[70px]">
          {sidebarV2 ? <SidebarV2 /> : <Sidebar minimize={sidebarMinimized} />}
          <ContentAdminV2>
            {shouldShowNewVersionBanner &&
              user.featureToggles.adminPanelV2Rollout &&
              sidebarV2 && <NewVersionBanner />}
            <Component />
          </ContentAdminV2>
        </div>
      </div>
    )
  }

  const status = requestErrorStatus(error)

  if (status === 500 && getCookie('access_token')) {
    removeCookieOnDomain('access_token')
    return <ServerErrorScreen />
  }
}

export default PrivateAdminRoute
